/**
 * Tax Invoice PDF — mirrors terminal receipt layout (Kotlin transactionReceipt).
 */
/* eslint-disable no-unused-vars */

import { PDFDocument, StandardFonts } from "pdf-lib";
import download from "downloadjs";

function money(value) {
  const num = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(num)) return "$ 0.00";
  return (
    "$ " +
    new Intl.NumberFormat("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)
  );
}

function num(value) {
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

function last12(id) {
  if (!id) return "";
  const s = String(id);
  return s.length > 12 ? s.slice(-12) : s;
}

function isTruthy(v) {
  return v === true || v === "true" || v === 1 || v === "1";
}

export default class PdfEditor {
  constructor() {
    this.fontSize = 10;
    this.lineGap = 3;
  }

  /**
   * @param {string} PDFName download basename (without .pdf)
   * @param {object} receipt receipt DTO (single transaction + items)
   * @param {object} [meta] { abn, voucherName }
   */
  async createTaxInvoice(PDFName, receipt, meta = {}) {
    const pdfDoc = await PDFDocument.create();
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);

    const pageWidth = 280;
    const margin = 24;
    const contentWidth = pageWidth - margin * 2;
    let page = pdfDoc.addPage([pageWidth, 720]);
    let { height } = page.getSize();
    let y = height - margin;
    const fontSize = this.fontSize;

    const ensureSpace = (needed = fontSize + this.lineGap) => {
      if (y - needed < margin) {
        page = pdfDoc.addPage([pageWidth, 720]);
        height = page.getSize().height;
        y = height - margin;
      }
    };

    const drawLeft = (text, opts = {}) => {
      const size = opts.size || fontSize;
      const font = opts.bold ? helveticaBold : helvetica;
      ensureSpace(size + this.lineGap);
      page.drawText(String(text ?? ""), {
        x: margin,
        y: y - size,
        size,
        font,
      });
      y -= size + this.lineGap;
    };

    const drawLeftRight = (left, right, opts = {}) => {
      const size = opts.size || fontSize;
      const font = opts.bold ? helveticaBold : helvetica;
      ensureSpace(size + this.lineGap);
      const leftText = String(left ?? "");
      const rightText = String(right ?? "");
      page.drawText(leftText, {
        x: margin,
        y: y - size,
        size,
        font,
      });
      const rightWidth = font.widthOfTextAtSize(rightText, size);
      page.drawText(rightText, {
        x: margin + contentWidth - rightWidth,
        y: y - size,
        size,
        font,
      });
      y -= size + this.lineGap;
    };

    const drawCentered = (text, opts = {}) => {
      const size = opts.size || fontSize;
      const font = opts.bold ? helveticaBold : helvetica;
      ensureSpace(size + this.lineGap);
      const t = String(text ?? "");
      const w = font.widthOfTextAtSize(t, size);
      page.drawText(t, {
        x: margin + (contentWidth - w) / 2,
        y: y - size,
        size,
        font,
      });
      y -= size + this.lineGap;
    };

    // Header
    drawLeft("Tax Invoice", { bold: true });
    drawLeft(receipt.EventName || "");
    drawLeft(receipt.StallholderName || receipt.StallHolderName || "");
    drawLeft(receipt.StallName || "");
    drawLeft(`ABN: ${meta.abn || ""}`);
    drawLeft(`Transaction No: ${last12(receipt.TransactionId)}`);
    drawLeft(`Terminal No: ${receipt.TerminalNumber ?? ""}`);
    drawLeft(`Terminal ID: ${receipt.DeviceId || ""}`);
    drawLeft(`Date: ${receipt.DateCreated || ""}`);
    drawLeft(`Time: ${receipt.TimeCreated || ""}`);
    y -= fontSize; // spacer before products

    const items = Array.isArray(receipt.TransactionItems)
      ? receipt.TransactionItems
      : [];
    const productItems = items.filter((i) => !isTruthy(i.ProductDiscountSurcharge));
    const discountItems = items.filter((i) => isTruthy(i.ProductDiscountSurcharge));

    // Group by category (Kotlin), then by product name + amount + gst
    const byCategory = {};
    productItems.forEach((item) => {
      const cat = item.CategoryName || item.categoryName || "";
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(item);
    });

    Object.keys(byCategory)
      .sort()
      .forEach((cat) => {
        if (cat) {
          drawLeft(cat, { bold: true });
        }
        const groups = {};
        byCategory[cat].forEach((item) => {
          const key = [
            item.ProductName || "",
            String(item.Amount ?? ""),
            String(isTruthy(item.GstFlag)),
            String(isTruthy(item.isManualProduct)),
          ].join("|");
          if (!groups[key]) groups[key] = [];
          groups[key].push(item);
        });
        Object.values(groups).forEach((group) => {
          const first = group[0];
          const qty = group.reduce((s, i) => s + num(i.Qty ?? i.qty ?? 1), 0);
          const gstTag = isTruthy(first.GstFlag) ? " *" : "";
          const name = first.ProductName || "";
          drawLeftRight(
            `  ${qty} ${name}${gstTag}`,
            `@ ${money(first.Amount)}`
          );
        });
      });

    y -= fontSize / 2;

    // Product discount / surcharge line
    const hasProductDiscount =
      isTruthy(receipt.ProductDiscountSurcharge) || discountItems.length > 0;
    if (hasProductDiscount) {
      const pct = num(receipt.ProductDiscountSurchargePercentage);
      const pctLabel = `${pct.toFixed(1)}%`;
      const discountName =
        (discountItems[0] && discountItems[0].ProductName) ||
        "Discount Surcharge Item";
      drawLeftRight(
        `${pctLabel} ${discountName}`,
        money(receipt.ProductDiscountSurchargeTotal)
      );
    }

    // Card surcharge — only if != 0
    const cardSurcharge = num(receipt.CardSurchargeTotal);
    if (cardSurcharge !== 0) {
      const pct = num(receipt.CardSurchargePercentage);
      drawLeftRight(
        `${pct}% Card Surcharge`,
        money(cardSurcharge)
      );
    }

    // Total + GST note
    const total = money(receipt.TotalAmount);
    drawLeftRight("Total:", total, { bold: true });
    const gst = num(receipt.TotalGST ?? receipt.GstTotal);
    if (gst !== 0) {
      drawLeft(`(* Includes GST of ${money(gst)})`);
    }

    y -= fontSize / 2;

    // Paid by
    const typeRaw = String(receipt.Type || "").toLowerCase();
    const typeLabel = typeRaw ? typeRaw.toUpperCase() : "";
    drawLeftRight("", `${total}  Paid By ${typeLabel}`);

    if (typeRaw === "card" && (receipt.Stan || receipt.STAN)) {
      drawLeftRight("", `STAN: ${receipt.Stan || receipt.STAN}`);
    }
    if (typeRaw === "voucher" && meta.voucherName) {
      drawLeftRight("", meta.voucherName);
    }

    // Order number — only if set
    const orderNo = receipt.OrderNumber;
    if (orderNo !== undefined && orderNo !== null && String(orderNo).trim() !== "") {
      y -= fontSize;
      drawCentered("Order Number:", { bold: true });
      drawCentered(String(orderNo), { bold: true, size: 14 });
    }

    const pdfBytes = await pdfDoc.save();
    download(pdfBytes, `${PDFName}.pdf`, "application/pdf");
    return pdfBytes;
  }

  /** @deprecated stub used by older call sites — prefer createTaxInvoice */
  async createPdf(PDFName, transactions) {
    const row = Array.isArray(transactions) ? transactions[0] : transactions;
    return this.createTaxInvoice(PDFName, {
      ...row,
      TransactionId: row.TransactionId || row.TransactionID,
      StallholderName: row.StallholderName || row.StallHolderName,
      TotalAmount: row.TotalAmount || row.Amount,
      TransactionItems: row.TransactionItems || [],
    });
  }
}
